import { useMutation, useQuery, useQueryClient } from 'react-query';
import { OrderFilm } from '../../OrderFilmApi';
import { getOrderFilms, orderFilmsQueryKey, removeFilmFile, sendFilm } from './orderFilms';

type MutationCallbacks = {
    onSuccess?: () => void;
    onError?: (error: unknown) => void;
};

type SendFilmVariables = {
    orderFilmId: number;
    email: string;
};

export function useOrderFilms(orderId: number) {
    return useQuery(orderFilmsQueryKey(orderId), () => getOrderFilms(orderId));
}

// Every film endpoint answers with the full list of ordered films, so a mutation
// result replaces the cached list instead of triggering a refetch.
export function useSetOrderFilms(orderId: number): (films: OrderFilm[]) => void {
    let queryClient = useQueryClient();

    return (films: OrderFilm[]) => {
        queryClient.setQueryData(orderFilmsQueryKey(orderId), films);
    };
}

export function useSendFilm(orderId: number, callbacks: MutationCallbacks = {}) {
    let setOrderFilms = useSetOrderFilms(orderId);

    return useMutation(
        ({ orderFilmId, email }: SendFilmVariables) => sendFilm(orderId, orderFilmId, email),
        {
            onSuccess: (films: OrderFilm[]) => {
                setOrderFilms(films);
                if (callbacks.onSuccess) {
                    callbacks.onSuccess();
                }
            },
            onError: (error: unknown) => {
                if (callbacks.onError) {
                    callbacks.onError(error);
                }
            }
        }
    );
}

export function useRemoveFilmFile(orderId: number, callbacks: MutationCallbacks = {}) {
    let setOrderFilms = useSetOrderFilms(orderId);

    return useMutation((orderFilmId: number) => removeFilmFile(orderId, orderFilmId), {
        onSuccess: (films: OrderFilm[]) => {
            setOrderFilms(films);
            if (callbacks.onSuccess) {
                callbacks.onSuccess();
            }
        },
        onError: (error: unknown) => {
            if (callbacks.onError) {
                callbacks.onError(error);
            }
        }
    });
}
